import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Search, ExternalLink, User, Ticket, Package, FileText, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function GlobalSearch({ open, onOpenChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (query.length >= 2) {
      const delayDebounce = setTimeout(() => {
        performSearch();
      }, 300);
      return () => clearTimeout(delayDebounce);
    } else {
      setResults(null);
    }
  }, [query]);

  const performSearch = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/search?query=${encodeURIComponent(query)}`);
      setResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (type, id) => {
    onOpenChange(false);
    setQuery('');
    
    switch(type) {
      case 'customer':
        navigate('/customers');
        break;
      case 'ticket':
        navigate('/tickets');
        break;
      case 'asset':
        navigate('/assets');
        break;
      case 'kb':
        navigate('/knowledge-base');
        break;
      default:
        break;
    }
  };

  const getIcon = (type) => {
    const icons = {
      customer: <User className="h-4 w-4 text-blue-600" />,
      ticket: <Ticket className="h-4 w-4 text-orange-600" />,
      asset: <Package className="h-4 w-4 text-green-600" />,
      knowledge_base: <BookOpen className="h-4 w-4 text-purple-600" />
    };
    return icons[type] || <FileText className="h-4 w-4 text-gray-600" />;
  };

  const getTotalResults = () => {
    if (!results) return 0;
    return (
      (results.customers?.length || 0) +
      (results.tickets?.length || 0) +
      (results.assets?.length || 0) +
      (results.knowledge_base?.length || 0)
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search CipherOne & MathewsIT.com
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search customers, tickets, assets, or MathewsIT.com..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 text-lg"
              autoFocus
            />
          </div>

          {loading && (
            <div className="text-center py-8 text-gray-500">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2">Searching...</p>
            </div>
          )}

          {query.length >= 2 && !loading && (
            <div className="space-y-4">
              {/* MathewsIT.com External Search */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Search MathewsIT.com</h3>
                      <p className="text-sm text-gray-600">Find information on the public website</p>
                    </div>
                    <a
                      href={`https://mathewsit.com/?s=${encodeURIComponent(query)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    >
                      Search Website
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* CipherOne Results */}
              {results && getTotalResults() > 0 ? (
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">CipherOne Results ({getTotalResults()})</h3>

                  {results.customers && results.customers.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Customers ({results.customers.length})
                      </h4>
                      <div className="space-y-2">
                        {results.customers.map((customer) => (
                          <div
                            key={customer.id}
                            onClick={() => handleResultClick('customer', customer.id)}
                            className="p-3 border rounded hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold">{customer.company_name}</p>
                                <p className="text-sm text-gray-600">{customer.email}</p>
                              </div>
                              <Badge>{customer.status}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {results.tickets && results.tickets.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                        <Ticket className="h-4 w-4" />
                        Tickets ({results.tickets.length})
                      </h4>
                      <div className="space-y-2">
                        {results.tickets.map((ticket) => (
                          <div
                            key={ticket.id}
                            onClick={() => handleResultClick('ticket', ticket.id)}
                            className="p-3 border rounded hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold">{ticket.title}</p>
                                <p className="text-sm text-gray-600">{ticket.ticket_number}</p>
                              </div>
                              <Badge>{ticket.status}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {results.assets && results.assets.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Assets ({results.assets.length})
                      </h4>
                      <div className="space-y-2">
                        {results.assets.map((asset) => (
                          <div
                            key={asset.id}
                            onClick={() => handleResultClick('asset', asset.id)}
                            className="p-3 border rounded hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold">{asset.name}</p>
                                <p className="text-sm text-gray-600">{asset.serial_number}</p>
                              </div>
                              <Badge>{asset.status}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {results.knowledge_base && results.knowledge_base.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                        <BookOpen className="h-4 w-4" />
                        Knowledge Base ({results.knowledge_base.length})
                      </h4>
                      <div className="space-y-2">
                        {results.knowledge_base.map((article) => (
                          <div
                            key={article.id}
                            onClick={() => handleResultClick('kb', article.id)}
                            className="p-3 border rounded hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <p className="font-semibold">{article.title}</p>
                            <p className="text-sm text-gray-600 line-clamp-2">{article.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : query.length >= 2 && !loading && results && (
                <div className="text-center py-8 text-gray-500">
                  <p>No results found in CipherOne</p>
                  <p className="text-sm mt-2">Try searching MathewsIT.com above</p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
